import gsap from 'gsap';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import Dispatch from '../../core/Dispatch';
import { COMPONENT_INIT } from '../../lib/events';
import Viewport from '../../core/Viewport';

gsap.registerPlugin(DrawSVGPlugin);
gsap.registerPlugin(MotionPathPlugin);

export default el => {

    const totalSteps = el.querySelectorAll('[data-content]').length;
    const modelEl = el.querySelector('[data-model]');

    let observer;
    let dashArrayTweens = [];

    const isVertical = () => window.getComputedStyle(modelEl.firstElementChild).flexDirection === 'column';

    const setScale = () => {
        el.style.setProperty('--scale', 1);
        const { width: modelWidth } = modelEl.getBoundingClientRect();
        const { width: innerModelWidth } = modelEl.firstElementChild.getBoundingClientRect();
        const scale = modelWidth / innerModelWidth;
        if (scale < 1) {
            el.style.setProperty('--scale', scale);
        } else {
            el.style.setProperty('--scale', 1);
        }
    };

    const getActiveStepIndex = () => {
        const contentNodes = el.querySelectorAll('[data-content]');
        const activeContent = el.querySelector('[data-content]:not([inert])');
        return Math.max([...contentNodes].indexOf(activeContent), 0);
    };

    const getStepIdByIndex = index => [...el.querySelectorAll('[data-content]')][index].id;

    const updateCounter = () => {
        el.querySelector('[data-current]').textContent = getActiveStepIndex() + 1;
    };

    const activatePaths = () => {
        const activeIndex = getActiveStepIndex();
        const activeLineArrow = modelEl.querySelector(`[data-arrowline="${activeIndex}"]`);
        if (!activeLineArrow || activeLineArrow.classList.contains('active')) {
            return;
        }
        modelEl.querySelectorAll('[data-arrowline].active').forEach(arrowLine => {
            arrowLine.classList.remove('active');
        });
        activeLineArrow.classList.add('active');
    };

    const setActiveStep = id => {
        const activePanel = document.getElementById(id);
        if (!activePanel.inert) {
            return;
        }
        el.querySelectorAll('button[aria-controls][aria-current]')
            .forEach(button => {
                button.removeAttribute('aria-current');
            });
        el.querySelectorAll(`button[aria-controls="${id}"]`)
            .forEach(button => {
                button.setAttribute('aria-current', 'true');
            });
        const inactivePanels = el.querySelectorAll(`[data-content]:not([id="${id}"]):not([inert])`);
        inactivePanels
            .forEach(content => {
                content.inert = true;
            });
        activePanel.inert = false;
        const tl = gsap.timeline()
            .timeScale(1.25);

        if (inactivePanels.length) {
            inactivePanels.forEach(panel => {
                tl
                    .set(panel, {
                        visibility: 'visible'
                    }, 'out')
                    .to(panel, {
                        opacity: 0,
                        duration: 0.5,
                        ease: 'Cubic.easeIn'
                    }, 'out')
                    .to(panel, {
                        y: -5,
                        duration: 0.5,
                        ease: 'Quint.easeIn'
                    }, 'out')
                    .set(panel, {
                        clearProps: 'visibility'
                    });
            });
        }
        tl
            .set(activePanel, {
                clearProps: 'visibility'
            }, 'in-=0.15')
            .fromTo(activePanel, {
                opacity: 0
            }, {
                opacity: 1,
                duration: 0.5,
                ease: 'Cubic.easeIn'
            }, 'in-=0.15')
            .fromTo(activePanel, {
                y: 5
            }, {
                y: 0,
                duration: 0.75,
                ease: 'Quint.easeOut'
            }, 'in-=0.15');

        updateCounter();
        activatePaths();
    };

    const onModelElementClick = e => {
        const id = e.target.getAttribute('aria-controls');
        setActiveStep(id);
    };

    const onResize = () => {
        setScale();
    };

    const onPrevBtnClick = () => {
        let activeStepIndex = getActiveStepIndex();
        if (!activeStepIndex) {
            activeStepIndex = totalSteps - 1;
        } else {
            activeStepIndex -= 1;
        }
        setActiveStep(getStepIdByIndex(activeStepIndex));
    };

    const onNextBtnClick = () => {
        let activeStepIndex = getActiveStepIndex();
        if (activeStepIndex >= totalSteps - 1) {
            activeStepIndex = 0;
        } else {
            activeStepIndex += 1;
        }
        setActiveStep(getStepIdByIndex(activeStepIndex));
    };

    const killDashArrayTweens = () => {
        dashArrayTweens.forEach(tween => {
            tween.kill();
        });
        dashArrayTweens = [];
    };

    const animateDashArrays = () => {
        killDashArrayTweens();
        const paths = modelEl.querySelectorAll('path[stroke-dasharray]');
        paths.forEach(path => {
            const length = MotionPathPlugin.getLength(path);
            const tween = gsap.to(path, {
                strokeDashoffset: -length,
                duration: length / 10,
                repeat: -1,
                ease: 'none'
            }, 0);
            dashArrayTweens.push(tween);
        });
    };

    const intro = () => {
        const tl = gsap.timeline();

        modelEl.querySelectorAll('[data-element]')
            .forEach((element, index) => {
                let label = `element${index}`;
                if (index) {
                    label += '-=0.15';
                }
                tl
                    .fromTo(element, {
                        opacity: 0
                    }, {
                        opacity: 1,
                        duration: 0.3,
                        ease: 'Cubic.easeIn'
                    }, label)
                    .fromTo(element, {
                        scale: 1.15
                    }, {
                        scale: 1,
                        duration: 0.5,
                        ease: 'Quad.easeOut'
                    }, label);

                const arrowLine = modelEl.querySelector(`[data-arrowline="${index}"]`);
                if (arrowLine) {
                    const arrowLineTl = gsap.timeline();
                    if (isVertical()) {
                        const verticalLinePath = arrowLine.querySelector('[data-line="vertical"] [data-line] svg path');
                        const verticalLineArrow = arrowLine.querySelector('[data-line="vertical"] [data-arrow]');
                        arrowLineTl
                            .fromTo(verticalLinePath, {
                                drawSVG: '0%'
                            }, {
                                drawSVG: '100%',
                                duration: 0.5,
                                ease: 'Quad.easeInOut'
                            }, 0)
                            .to(verticalLineArrow, {
                                motionPath: {
                                    path: verticalLinePath,
                                    align: verticalLinePath,
                                    alignOrigin: [0, 0.5]
                                },
                                duration: 0.5,
                                ease: 'Quad.easeInOut'
                            }, 0)
                            .fromTo(verticalLineArrow, {
                                scale: 0
                            }, {
                                scale: 1,
                                duration: 0.3,
                                transformOrigin: 'left center'
                            }, 0);
                    } else {
                        const horizontalLinePath = arrowLine.querySelector('[data-line="horizontal"] [data-line] svg path');
                        const horizontalLineArrow = arrowLine.querySelector('[data-line="horizontal"] [data-arrow]');
                        arrowLineTl
                            .fromTo(horizontalLinePath, {
                                drawSVG: '0%'
                            }, {
                                drawSVG: '100%',
                                duration: 0.75,
                                ease: 'Quad.easeInOut'
                            }, 0)
                            .to(horizontalLineArrow, {
                                motionPath: {
                                    path: horizontalLinePath,
                                    align: horizontalLinePath,
                                    alignOrigin: [0, 0.5]
                                },
                                duration: 0.75,
                                ease: 'Quad.easeInOut'
                            }, 0)
                            .fromTo(horizontalLineArrow, {
                                scale: 0
                            }, {
                                scale: 1,
                                duration: 0.3,
                                transformOrigin: 'left center'
                            }, 0.3);
                    }
                    arrowLineTl.add(() => {
                        arrowLine.classList.add('initialised');
                    }, '-=0.3');
                    tl.add(arrowLineTl, label);
                }
            });
    };

    const init = () => {
        setScale();

        modelEl.querySelectorAll('button[aria-controls]')
            .forEach(button => {
                button.addEventListener('click', onModelElementClick);
            });

        el.querySelector('button[data-nav="prev"]')
            .addEventListener('click', onPrevBtnClick);
        el.querySelector('button[data-nav="next"]')
            .addEventListener('click', onNextBtnClick);

        observer = new IntersectionObserver(([{ isIntersecting }]) => {
            if (!isIntersecting) {
                return;
            }
            observer.disconnect();
            observer = null;
            activatePaths();
            animateDashArrays();
            intro();
        });
        observer.observe(modelEl);

        Viewport.on('resize', onResize);

        Dispatch.emit(COMPONENT_INIT);
    };

    const destroy = () => {
        modelEl.querySelectorAll('button[aria-controls]')
            .forEach(button => {
                button.removeEventListener('click', onModelElementClick);
            });

        el.querySelector('button[data-nav="prev"]')
            .removeEventListener('click', onPrevBtnClick);
        el.querySelector('button[data-nav="next"]')
            .removeEventListener('click', onNextBtnClick);

        killDashArrayTweens();

        Viewport.on('resize', onResize);
    };

    return {
        init,
        destroy
    };

};
